import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Award, 
  CheckCircle, 
  XCircle, 
  Clock,
  Search,
  Filter,
  X,
  Save,
  DollarSign
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { Base_Url } from '../config/domainConfig';
import './FundingResultsManagement.css';

const API_BASE_URL = `${Base_Url}/api`;

const FundingResultsManagement = () => {
  const { showSuccess, showError } = useToast();
  const [results, setResults] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    period_id: '',
    applicant_name: '',
    company_name: '',
    business_category: '',
    funding_amount: '',
    description: '',
    status: 'announced',
    display_order: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch funding results
      const resultsResponse = await fetch(`${API_BASE_URL}/funding-results`);
      const resultsData = await resultsResponse.json();
      
      if (resultsData.success) {
        setResults(Array.isArray(resultsData.data) ? resultsData.data : []);
      } else {
        showError('Failed to fetch funding results');
      }

      // Fetch application periods
      const periodsResponse = await fetch(`${API_BASE_URL}/application-periods`);
      const periodsData = await periodsResponse.json();
      
      if (periodsData.success) {
        setPeriods(Array.isArray(periodsData.data) ? periodsData.data : []);
      } else {
        showError('Failed to fetch application periods');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showError('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const filteredResults = results.filter(result => {
    const matchesSearch = result.applicant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         result.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         result.business_category?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || result.status === statusFilter;
    const matchesPeriod = !periodFilter || result.period_id.toString() === periodFilter;
    return matchesSearch && matchesStatus && matchesPeriod;
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.period_id) {
      errors.period_id = 'Application period is required';
    }
    if (!formData.applicant_name.trim()) {
      errors.applicant_name = 'Applicant name is required';
    }
    if (!formData.funding_amount || parseFloat(formData.funding_amount) <= 0) {
      errors.funding_amount = 'Valid funding amount is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddResult = async () => {
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/funding-results`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          funding_amount: parseFloat(formData.funding_amount),
          display_order: parseInt(formData.display_order) || 0
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        showSuccess('Funding result created successfully');
        setShowAddModal(false);
        resetForm();
        fetchData();
      } else {
        showError(data.message || 'Failed to create funding result');
      }
    } catch (error) {
      console.error('Error creating result:', error);
      showError('Error creating funding result');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditResult = async () => {
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/funding-results/${selectedResult.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          funding_amount: parseFloat(formData.funding_amount),
          display_order: parseInt(formData.display_order) || 0
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        showSuccess('Funding result updated successfully');
        setShowEditModal(false);
        resetForm();
        fetchData();
      } else {
        showError(data.message || 'Failed to update funding result');
      }
    } catch (error) {
      console.error('Error updating result:', error);
      showError('Error updating funding result');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteResult = async () => {
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/funding-results/${selectedResult.id}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        showSuccess('Funding result deleted successfully');
        setShowDeleteModal(false);
        fetchData();
      } else {
        showError(data.message || 'Failed to delete funding result');
      }
    } catch (error) {
      console.error('Error deleting result:', error);
      showError('Error deleting funding result');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (result) => {
    setSelectedResult(result);
    setFormData({
      period_id: result.period_id.toString(),
      applicant_name: result.applicant_name,
      company_name: result.company_name || '',
      business_category: result.business_category || '',
      funding_amount: result.funding_amount.toString(),
      description: result.description || '',
      status: result.status,
      display_order: result.display_order.toString()
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const openDeleteModal = (result) => {
    setSelectedResult(result);
    setShowDeleteModal(true);
  };

  const resetForm = () => {
    setFormData({
      period_id: '',
      applicant_name: '',
      company_name: '',
      business_category: '',
      funding_amount: '',
      description: '',
      status: 'announced',
      display_order: ''
    });
    setFormErrors({});
    setSelectedResult(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      announced: { label: 'Announced', className: 'status-announced', icon: <CheckCircle size={16} /> },
      pending: { label: 'Pending', className: 'status-pending', icon: <Clock size={16} /> }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`status-badge ${config.className}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="funding-results-management">
        <div className="loading-spinner">Loading funding results...</div>
      </div>
    );
  }

  return (
    <div className="funding-results-management">
      <div className="management-header">
        <h2>Funding Results Management</h2>
        <button 
          className="add-result-btn"
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
        >
          <Plus size={20} />
          Add New Result
        </button>
      </div>

      {/* Search and Filter */}
      <div className="search-filter-container">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search results..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-box">
          <Filter size={20} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="announced">Announced</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        <div className="filter-box">
          <Filter size={20} />
          <select
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
          >
            <option value="">All Periods</option>
            {periods.map(period => (
              <option key={period.id} value={period.id.toString()}>
                {period.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results Table */}
      <div className="results-table-container">
        <table className="results-table">
          <thead>
            <tr>
              <th>Applicant</th>
              <th>Company</th>
              <th>Category</th>
              <th>Amount</th>
              <th>Period</th>
              <th>Status</th>
              <th>Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredResults.map(result => (
              <tr key={result.id}>
                <td>
                  <div className="applicant-info">
                    <h4>{result.applicant_name}</h4>
                    {result.description && (
                      <p className="result-description">{result.description}</p>
                    )}
                  </div>
                </td>
                <td>{result.company_name || '-'}</td>
                <td>{result.business_category || '-'}</td>
                <td>
                  <div className="amount-display">
                    <DollarSign size={16} />
                    {formatAmount(result.funding_amount)}
                  </div>
                </td>
                <td>{result.period_title}</td>
                <td>{getStatusBadge(result.status)}</td>
                <td>{result.display_order}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="edit-btn"
                      onClick={() => openEditModal(result)}
                      title="Edit Result"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => openDeleteModal(result)}
                      title="Delete Result"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredResults.length === 0 && (
          <div className="no-data">
            <p>No funding results found</p>
          </div>
        )}
      </div>

      {/* Add Result Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Funding Result</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Application Period *</label>
                  <select
                    name="period_id"
                    value={formData.period_id}
                    onChange={handleInputChange}
                    className={formErrors.period_id ? 'error' : ''}
                  >
                    <option value="">Select Period</option>
                    {periods.map(period => (
                      <option key={period.id} value={period.id.toString()}>
                        {period.title}
                      </option>
                    ))}
                  </select>
                  {formErrors.period_id && <span className="error-message">{formErrors.period_id}</span>}
                </div>
                
                <div className="form-group">
                  <label>Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="announced">Announced</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Applicant Name *</label>
                  <input
                    type="text"
                    name="applicant_name"
                    value={formData.applicant_name}
                    onChange={handleInputChange}
                    className={formErrors.applicant_name ? 'error' : ''}
                  />
                  {formErrors.applicant_name && <span className="error-message">{formErrors.applicant_name}</span>}
                </div>
                
                <div className="form-group">
                  <label>Company Name</label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Business Category</label>
                  <input
                    type="text"
                    name="business_category"
                    value={formData.business_category}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className="form-group">
                  <label>Funding Amount *</label>
                  <input
                    type="number"
                    name="funding_amount"
                    value={formData.funding_amount}
                    onChange={handleInputChange}
                    className={formErrors.funding_amount ? 'error' : ''}
                    min="0"
                    step="0.01"
                  />
                  {formErrors.funding_amount && <span className="error-message">{formErrors.funding_amount}</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  name="display_order"
                  value={formData.display_order}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowAddModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleAddResult}
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create Result'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Result Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Funding Result</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Application Period *</label>
                  <select
                    name="period_id"
                    value={formData.period_id}
                    onChange={handleInputChange}
                    className={formErrors.period_id ? 'error' : ''}
                  >
                    <option value="">Select Period</option>
                    {periods.map(period => (
                      <option key={period.id} value={period.id.toString()}>
                        {period.title}
                      </option>
                    ))}
                  </select>
                  {formErrors.period_id && <span className="error-message">{formErrors.period_id}</span>}
                </div>
                
                <div className="form-group">
                  <label>Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="announced">Announced</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Applicant Name *</label>
                  <input
                    type="text"
                    name="applicant_name"
                    value={formData.applicant_name}
                    onChange={handleInputChange}
                    className={formErrors.applicant_name ? 'error' : ''}
                  />
                  {formErrors.applicant_name && <span className="error-message">{formErrors.applicant_name}</span>}
                </div>
                
                <div className="form-group">
                  <label>Company Name</label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Business Category</label>
                  <input
                    type="text"
                    name="business_category"
                    value={formData.business_category}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className="form-group">
                  <label>Funding Amount *</label>
                  <input
                    type="number"
                    name="funding_amount"
                    value={formData.funding_amount}
                    onChange={handleInputChange}
                    className={formErrors.funding_amount ? 'error' : ''}
                    min="0"
                    step="0.01"
                  />
                  {formErrors.funding_amount && <span className="error-message">{formErrors.funding_amount}</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  name="display_order"
                  value={formData.display_order}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowEditModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleEditResult}
                disabled={submitting}
              >
                {submitting ? 'Updating...' : 'Update Result'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Funding Result</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to delete the funding result for "<strong>{selectedResult?.applicant_name}</strong>"?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowDeleteModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                className="btn-danger" 
                onClick={handleDeleteResult}
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete Result'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FundingResultsManagement; 